import { useMemo, useState } from "react";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import CartBannerStrip from "components/common/notification-strips/CartBannerStrip";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { useJsonPermission } from "lib/hooks/useJsonPermission";
import { useLocale } from "lib/hooks/useLocale";
import { AuthenticatedArgs, BannerType } from "lib/types/checkout";
import { analyticsEvents, eventTypes } from "lib/utils/constants";
import { currencyFormatter } from "lib/utils/formatters";
import {
  classNames,
  shouldRedirectToParent,
  mapRedirectCheckoutLineItems,
  publishPostMessage,
} from "lib/utils/helpers";
import { errorToast } from "lib/utils/toasters";
import { motion, AnimatePresence } from "motion/react";
import { useToken } from "lib/contexts/TokenProvider";
import { createCartToCheckoutURL, isCartRedirectEnabled } from "lib/utils/checkout";
import Price from "components/common/Price";
import { Icon } from "@shopflo/ui/icon";
import { CaretUp, Receipt } from "@phosphor-icons/react";
import { AddOnButton } from "components/common/AddOnButton";
import ShopfloBadge from "components/common/ShopfloBadge";
import { getTextColorByBrightness, hexToRgba } from "@shopflo/ui/utils";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";

// Skeleton loader component
const CartFooterSkeleton = () => {
  return (
    <div className="px-2py-4 bg-transparent space-y-2">
      <div className="skeleton-loader h-[16px] rounded-md w-2/5"></div>
      <div className="skeleton-loader h-[14px] rounded-md w-4/5"></div>
    </div>
  );
};

export const CartFooter = ({
  setDialog,
  isLoading = false,
  primaryCTAText = "Checkout",
  setIsUpdating,
  bottomBannerConfig,
}: AuthenticatedArgs) => {
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: {
      checkoutItems,
      appliedCoupons,
      billing,
      checkoutId,
      availableAddOns,
      appliedCartAddOns: cartLevelAddOns,
    },
  } = useCheckoutContext();
  const {
    state: { merchant, cartMetadata },
  } = useMerchantContext();
  const {
    state: { tokenId },
  } = useToken();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const { handleAddOnEdit } = useItemEdit({
    setIsUpdating,
    isCartItem: true,
  });
  const { cartPermissions } = useJsonPermission();
  const { t } = useLocale();

  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);

  const hideCartTotal = merchant?.hideCartTotal;
  const addOnsList = useMemo(() => {
    return Object.values(availableAddOns ?? {}).filter(
      (item) =>
        item?.addon_level === "CART" &&
        ["CART", "DEFAULT"].includes(item?.ui_asset) &&
        item.addon_type !== "CART_NOTE" &&
        !cartLevelAddOns.some((addOn) => addOn?.addon_id === item?.addon_id),
    );
  }, [availableAddOns, cartLevelAddOns]);

  const totalDiscountedValue = billing?.discount + (billing?.original_sub_total - billing?.sub_total);
  const { uiAttributes: { footer, addonConfig } = {}, primaryCTAConfig } = cartMetadata ?? {};
  const gradient = `linear-gradient(to right, ${footer?.savingsColor}, ${footer?.savingsColor}, #000000, ${footer?.savingsColor}, ${footer?.savingsColor})`;

  const handleAuthenticatedOrSkipCartAuthentication = async () => {
    try {
      publishPostMessage(eventTypes.CART_TO_CHECKOUT, {
        checkoutId: checkoutId,
        lineItems: checkoutItems,
        cartTotal: billing.total_payable,
        coupons: appliedCoupons,
        tokenId,
      });

      if (isCartRedirectEnabled()) {
        const redirectUrl = createCartToCheckoutURL(document.location.href, checkoutId);
        window.location.href = redirectUrl.href;
        return;
      }
    } catch (error) {
      console.error("error in going to checkout", error);
      errorToast("Failed loading checkout");
    } finally {
    }
  };

  const handleCheckoutClick = () => {
    sendAnalyticsEvent({
      eventName: analyticsEvents.FLO_CHECKOUT_CLICKED,
      eventType: "click",
    });
    if (merchant?.customCartCheckoutEnabled) {
      publishPostMessage(eventTypes.CART_TO_CUSTOM_CHECKOUT, {
        checkoutId,
        tokenId,
      });
      return;
    }
    const redirectToParent = shouldRedirectToParent(merchant?.disableThirdPartyCookieRedirect);
    if (merchant?.cart?.skipLoginPopup || (isAuthenticated && !redirectToParent)) {
      handleAuthenticatedOrSkipCartAuthentication();
    } else if (redirectToParent) {
      const redirectUrl = createCartToCheckoutURL(document.location.href, checkoutId);
      publishPostMessage(eventTypes.PARENT_REDIRECT, {
        redirectUrl: redirectUrl.href,
        clearCart: false,
        tokenId,
        isCartToCheckout: true,
        eventPayload: {
          name: "InitiateCheckout",
          checkoutId: checkoutId,
          orderTotal: billing.total_payable,
          shippingCharge: billing.shipping,
          totalTax: billing.tax,
          lineItems: mapRedirectCheckoutLineItems(checkoutItems),
          coupons: appliedCoupons.length > 0 ? appliedCoupons[0].title : "",
          currency: "INR",
        },
      });
    } else {
      setDialog("authentication");
    }
  };

  const getCartTotal = () => {
    if (!tokenId) {
      return <CartFooterSkeleton />;
    }

    return (
      <div className="w-full flex justify-between items-center pb-2">
        <div className="flex items-center gap-1">
          <Icon
            className="text-zinc-500 text-opacity-60"
            IconComponent={Receipt}
            size="sm"
            iconProps={{ weight: "fill", color: footer?.textColor, opacity: 0.4 }}
          />
          <div className="text-text-sm-medium text-zinc-700" style={{ color: footer?.textColor }}>
            {t("estimated_total")}
          </div>
        </div>
        <div className="flex items-start gap-1">
          <div className="flex flex-col items-end">
            <Price
              orientation="horizontal"
              size="lg"
              total={billing?.total_payable}
              compareAt={
                Boolean(cartMetadata?.uiAttributes?.layout?.metadata?.showOriginalPrice)
                  ? billing?.original_sub_total
                  : billing?.sub_total + billing?.addon_total
              } customization={{ textColor: footer?.textColor }}
            />
            {totalDiscountedValue > 0 && (
              <div
                className="text-text-xs-semibold animate-gradient-sweep"
                style={{
                  background: gradient,
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 2px 12px rgba(44, 135, 74, 0.4)",
                }}>
                <span
                  className="text-text-xs-medium"
                  style={{ textShadow: `0 2px 12px ${hexToRgba(footer?.savingsColor ?? "#2C874A", 0.35)}` }}>
                  You saved
                </span>{" "}
                <span style={{ textShadow: "0 2px 12px rgba(44, 135, 74, 0.4)" }}>
                  {currencyFormatter(totalDiscountedValue)}!
                </span>
              </div>
            )}
          </div>
          <span className="mt-2">
            <Icon
              IconComponent={CaretUp}
              size="xxs"
              className={classNames(
                "transition-transform duration-300",
                isSummaryExpanded ? "rotate-180" : "",
              )}
              iconProps={{ weight: "bold", color: hexToRgba(footer?.textColor ?? "#000000", 0.6) }}
            />
          </span>
        </div>
      </div>
    );
  };

  const getCartSummary = () => {
    if (!billing) return null;

    const renderRow = (label: string, value: string, isDiscount?: boolean) => {
      const valueColor = isDiscount ? "text-yay-dark" : "text-zinc-600";

      return (
        <div className="relative flex items-center justify-between gap-2 px-1 py-1.5">
          <div
            className="absolute top-0 left-0 right-0 border-t-2 border-dotted border-zinc-900/10"
            style={{
              borderColor: getTextColorByBrightness({
                bgHexColor: footer?.backgroundColor ?? "#FFFFFF",
                lightOpacity: 24,
              }),
            }}
          />
          <span
            className="text-[12px] font-medium leading-[18px] text-zinc-600"
            style={{
              color: footer?.textColor,
              opacity: 0.8,
            }}>
            {label}
          </span>
          <span
            className="text-[12px] font-medium leading-[18px] text-right"
            style={{
              color: isDiscount ? footer?.savingsColor : footer?.textColor,
              opacity: 0.8,
            }}>
            {value}
          </span>
        </div>
      );
    };

    return (
      <div className="flex flex-col">
        {renderRow(
          "Total MRP",
          currencyFormatter(
            Boolean(cartMetadata?.uiAttributes?.layout?.metadata?.showOriginalPrice)
              ? billing?.original_sub_total
              : billing?.sub_total + billing?.addon_total,
          ),
        )}
        {renderRow(
          t("delivery_fee"),
          Boolean(billing.shipping) && billing.shipping >= 0
            ? currencyFormatter(billing?.shipping)
            : t("to_be_calculated"),
        )}
        {renderRow(
          "Discount on MRP",
          currencyFormatter(billing?.original_sub_total - billing?.sub_total),
          true,
        )}
        {renderRow(t("discount"), currencyFormatter(billing?.discount ?? ""), true)}
        {renderRow(t("convenience_fee"), currencyFormatter(billing?.convenience_fee ?? ""), true)}
        {renderRow(t("grand_total"), currencyFormatter(billing.total_payable))}
        {totalDiscountedValue > 0 && (
          <div
            className="mt-1 flex items-center justify-center gap-2 border-t-2 border-dotted border-zinc-900/10 min-h-12"
            style={{
              backgroundImage: `linear-gradient(90deg, transparent 0%, color-mix(in srgb, ${footer?.savingsColor} 17%, transparent) 50%, transparent 100%)`,
            }}>
            <p
              className="px-1 text-center text-[12px] font-semibold leading-[18px] text-yay-dark"
              style={{
                color: footer?.savingsColor,
              }}>
              You Saved {currencyFormatter(totalDiscountedValue)} (
              {((totalDiscountedValue / billing?.original_sub_total) * 100).toFixed(0)}%) so far!
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div id="cart-footer-section">
        {addOnsList?.length > 0 && (
          <motion.div
            layout
            initial={{ height: "auto", opacity: 1 }}
            animate={{
              height: isSummaryExpanded ? 0 : "auto",
              opacity: isSummaryExpanded ? 0 : 1,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              mass: 1,
            }}
            className="overflow-hidden bg-zinc-950/5"
            style={{
              backgroundColor: addonConfig?.backgroundColor ?? "#FFFFFF",
            }}>
            <div
              className="flex flex-col py-1.5 border-t-1"
              style={{
                borderColor: getTextColorByBrightness({
                  bgHexColor: addonConfig?.backgroundColor ?? "#FFFFFF",
                  lightOpacity: 24,
                }),
              }}>
              {addOnsList.map((item) => (
                <motion.div key={item.addon_id} layout>
                  <AddOnButton
                    customisations={addonConfig}
                    apiActionLoading={isLoading}
                    key={item?.addon_id}
                    addOnData={item}
                    handleClick={() => {
                      handleAddOnEdit({
                        action: "ADD",
                        addOnLevel: "CART",
                        addOnId: item?.addon_id,
                        addOnPrice: item?.price,
                      });
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
        {bottomBannerConfig?.enabled && (
          <CartBannerStrip bannerPosition={BannerType.FOOTER} bannerConfig={bottomBannerConfig} />
        )}
        <div
          className={classNames("flex flex-col px-4 pt-2 [&>*:last-child]:!mt-0")}
          style={{
            backgroundColor: footer?.backgroundColor,
            color: footer?.textColor,
            boxShadow:
              bottomBannerConfig?.enabled || addOnsList?.length
                ? "none"
                : "0px -1px 5px 0px rgba(175, 175, 175, 0.3)",
          }}>
          <div className="item-center flex flex-col justify-between">
            {!hideCartTotal && (
              <div className="w-full">
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setIsSummaryExpanded(!isSummaryExpanded);
                  }}>
                  {getCartTotal()}
                </div>
                <AnimatePresence>
                  {isSummaryExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                        mass: 1,
                      }}
                      className="overflow-hidden">
                      {getCartSummary()}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
          <PrimaryButton
            containerClassName="min-w-40"
            buttonText={primaryCTAText}
            onClick={handleCheckoutClick}
            isLoading={isLoading}
            isDisabled={
              merchant?.customCartCheckoutEnabled ? false : cartPermissions?.checkoutBtnStatus?.length > 0
            }
            showUpiIcon={cartMetadata?.uiAttributes?.showUpiIconsOnCartButton}
            customization={{
              backgroundColor:
                primaryCTAConfig?.backgroundColor ?? merchant?.colorPallet?.primaryColor ?? "#000000",
              textColor: primaryCTAConfig?.textColor ?? merchant?.colorPallet?.primaryTextColor ?? "#FFFFFF",
              borderRadius: primaryCTAConfig?.borderRadius ?? 12,
            }}
          />
          <div className="flex justify-center items-center py-2.5">
            <ShopfloBadge
              textColor={getTextColorByBrightness({
                bgHexColor: footer?.backgroundColor ?? "#FFFFFF",
                darkOpacity: 50,
                lightOpacity: 40,
              })}
            />
          </div>
        </div>
      </div>
      {isLoading && <OverlaySpinner />}
    </>
  );
};
